import getDealerProductApi from '@/api/get/company/dealer-product/get-dealer-product';
import getDealerQuoteApi from '@/api/get/company/sales-flow/get-dealer-quote';
import updateDealerQuoteApi from '@/api/patch/dealer/update-dealer-quote';
import getSupplierUEImageApi from '@/api/post/dealer/get-ue-images';
import createQuote from '@/api/post/quote/create-quote';
import Alert from '@/components/alert/alert-component';
import { PrimaryOutlineButton } from '@/components/button/button-component';
import ImageCarousel from '@/components/image-carousel/image-carousel-component';
import Loader from '@/components/loader/loader-component';
import PageWrapper from '@/components/page-wrapper/page-wrapper-component';
import { useLazyQuery, useMutation, useQuery } from '@/hooks/use-api';
import { recursiveFlatMap } from '@/pages/company-management/company-products/utils';
import { APP_CONTEXT } from '@/utils/context';
import { Heading2Text, SubheaderText } from '@/utils/global-fonts';
import {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaCircle } from 'react-icons/fa';
import {
  MdOutlineArrowBack,
  MdOutlineArrowForward,
  MdOutlineFileDownload,
} from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { ProductOptionsProvider } from './config-context';
import Styled from './configuration-style';
import RenderOption from './render-options-v2';
import recursiveGetOptionName from './utils';
import DependencyModal from './config-components/dependency-modal.component';

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_SHOW_DEPENDENCIES': {
      const { node } = action.payload;
      if (state.queue.length === 0) {
        return {
          ...state,
          show: action.payload,
          node,
          queue: [],
        };
      }
      const found = state.queue.find((x) => x.id === node.id);

      if (!found) {
        return {
          ...state,
          show: action.payload,
          node,
        };
      }
      return {
        ...state,
        queue: [...state.queue, action.payload],
      };
    }
    case 'ADD': {
      const found = state.queue.find((x) => x.id === action.payload.id);

      if (!found) {
        return {
          ...state,
          queue: [...state.queue, action.payload],
        };
      }
      return state;
    }
    case 'DONE': {
      if (state.queue.length === 0) {
        return {
          ...state,
          node: null,
          show: null,
        };
      }
      if (state.queue[0]) {
        const newNode = state.queue[0];
        const newQueue = state.queue;
        newQueue.shift();
        return {
          ...state,
          node: newNode,
          show: true,
          queue: newQueue,
        };
      }
      const newQueue = state.queue;
      newQueue.shift();
      return {
        ...state,
        node: null,
        show: null,
        queue: newQueue,
      };
    }
    default: {
      return state;
    }
  }
};

// FOURTH STEP
/**
 * Configuration component for rendering and managing product configurations
 *
 * @returns {JSX.Element} The rendered configuration component
 */
const Configuration = () => {
  const [slideIndex, setSlideIndex] = useState(0);
  const navigate = useNavigate();
  const { state } = useLocation();
  const [showDependencies, setShowDependencies] = useReducer(reducer, {
    show: false,
    node: null,
    queue: [],
  });
  const {
    selectedProduct,
    selectedProductCategory,
    selectedSupplier,
    duplicate,
  } = state || {};
  const [savingQuote, setSavingQuote] = useState(false);
  const { data, loading: dataLoading } = useQuery(
    getDealerProductApi,
    selectedSupplier?.id,
    selectedProduct?.id,
  );
  const { setShowSideNav } = useContext(APP_CONTEXT);
  const {
    refetch,
    data: quoteData,
    loading: quoteLoading,
  } = useLazyQuery(getDealerQuoteApi);
  const [createConfigQuote, { loading }] = useMutation(createQuote);
  const [updateConfigQuote, { loading: updateConfigLoading }] =
    useMutation(updateDealerQuoteApi);
  const [ueImage, { error }] = useMutation(getSupplierUEImageApi);
  const methods = useForm({
    mode: 'onSubmit',
  });
  const { handleSubmit, watch, getValues, setValue } = methods;
  const [ueImages, setUeImages] = useState([]);
  const [ueInitialLoad, setInitialLoad] = useState(true);
  const [ueOnline, setUeOnline] = useState(true);
  const [expandedOption, setExpandedOption] = useState(null);
  const watchedValues = watch();

  const processNodes = (nodes) => {
    const filteredNodes = nodes
      ?.filter(
        (node) =>
          (node.id !== undefined && node.id !== null) ||
          node.belowTheLine === true,
      )
      ?.map((node) => ({
        product_option_link_id: node.id ?? node.node,
        below_the_line:
          node.belowTheLine !== undefined ? node.belowTheLine : false,
      }));
    return filteredNodes;
  };

  const formatDependencies = (nodes) => {
    const extractedDependencies = recursiveFlatMap(nodes.options)
      ?.flatMap((x) => x?.dependencies)
      ?.filter((x) => x?.length > 0);

    const uniqueDependenciesArray = extractedDependencies.map((x) => ({
      product_option_link_id: x,
      below_the_line: false,
    }));

    return uniqueDependenciesArray;
  };

  const goToMainSummary = (responseQuoteId) => {
    navigate(`/shop/sales/main-summary/${responseQuoteId}/summary`, {
      state: {
        ...state,
        productOptions: data?.productOptions,
        quoteId: responseQuoteId,
        ueImages,
      },
    });
  };

  const onSubmit = async (values) => {
    const dependencies = formatDependencies(values);
    const formattedValues = processNodes(recursiveFlatMap(values.options));
    const concatOpts = [...dependencies, ...formattedValues];
    const allValues = concatOpts.filter(
      (option, index, self) =>
        index ===
        self.findIndex(
          (o) => o.product_option_link_id === option.product_option_link_id,
        ),
    );
    setSavingQuote(true);
    if (state?.quoteId && !duplicate) {
      updateConfigQuote(state?.quoteId, {
        quote_options: {
          add: allValues.filter(
            (dep) =>
              !quoteData?.quote_options.some(
                (opt) =>
                  opt.product_to_option_link_id ===
                    dep.product_option_link_id &&
                  opt.below_the_line === dep.below_the_line,
              ),
          ),
          remove: quoteData?.quote_options
            .filter(
              (dep) =>
                !allValues.some(
                  (opt) =>
                    opt.product_option_link_id ===
                    dep.product_to_option_link_id,
                ),
            )
            .map((x) => x.id),
        },
      })
        .then((res) => {
          setShowDependencies({
            type: 'SET_SHOW_DEPENDENCIES',
            payload: { show: false, node: null },
          });
          goToMainSummary(res.data.id);
        })
        .catch((e) => {
          Alert({
            icon: 'error',
            text: e.message,
          });
        });
    } else {
      const quoteSubmitData = {
        quote: {
          product_id: selectedProduct?.id,
          quantity: 1,
          discount: 0,
        },
        quote_options: {
          add: allValues,
        },
      };

      if (duplicate) {
        const { id, status, ...duplicateQuote } = quoteData.quote;
        quoteSubmitData.quote = {
          ...duplicateQuote,
          ...quoteSubmitData.quote,
        };

        const combinedQuoteModules = [
          quoteData?.quote_modules_prices,
          quoteData?.quote_modules_details,
        ].flat();

        quoteSubmitData.quote_modules = {
          update: combinedQuoteModules.map((item) => ({
            quote_module_id: item?.quote_module_id,
            price: item?.price,
            value: item?.value,
          })),
        };
      }

      createConfigQuote(quoteSubmitData)
        .then((res) => {
          setShowDependencies({
            type: 'SET_SHOW_DEPENDENCIES',
            payload: { show: false, node: null },
          });
          goToMainSummary(res.data.id);
        })
        .catch((e) => {
          Alert({
            icon: 'error',
            text: e.message,
          });
        });
    }
  };

  const isEmpty = useMemo(
    () =>
      processNodes(recursiveFlatMap(watchedValues?.options ?? []))?.length ===
      0,
    [watchedValues],
  );
  const overrideUnrealImages = useMemo(
    () => data?.product.override_unreal_images,
    [data?.product.override_unreal_images],
  );
  const fetchUEImages = useDebouncedCallback(() => {
    if (!overrideUnrealImages && !showDependencies.show) {
      const watchedOptionsValues = getValues('options');
      const flattenRootOptions = recursiveFlatMap(
        data?.productOptions ?? [],
        [],
        true,
      );
      // Function to collect all dependencies in the tree
      const getDependencies = (node) => {
        let result = [];
        if (node.id) {
          const rootOption = flattenRootOptions.find((x) => x.id === node.id);
          if (rootOption?.dependenciesPartNumbers?.length > 0) {
            result.push(rootOption.dependenciesPartNumbers);
          }
          if (node.children) {
            node.children.forEach((child) => {
              result = result.concat(getDependencies(child));
            });
          }
        }
        return result.flat();
      };

      // Function to flatten the tree and collect part numbers
      const flattenTree = (node) => {
        let result = [];
        if (node.partNumber && node.type && node.id) {
          result.push(node.partNumber);
        }
        if (node?.children) {
          node.children.forEach((child) => {
            if (child && child.id) {
              result = result.concat(flattenTree(child));
            }
          });
        }
        return result;
      };

      // Get all dependencies in the watchedOptionsValues
      const allDependencies = watchedOptionsValues.flatMap(getDependencies);

      // Extract part numbers from matchedNodes
      const dependencyPartNumbers = allDependencies.map(
        (node) =>
          // setValue(`${node.name}.id`, node.node);
          node.part_number,
      );

      // Flatten the tree to collect all part numbers
      const flattenedPartNumbers = watchedOptionsValues.flatMap(flattenTree);

      // Combine part numbers from the tree and dependencies
      const combinedPartNumbers = flattenedPartNumbers.concat(
        dependencyPartNumbers,
      );
      // Remove duplicates by converting to a Set and back to an array
      const uniquePartNumbers = [...new Set(combinedPartNumbers)];

      if (uniquePartNumbers.length > 0) {
        ueImage(selectedProduct?.id, uniquePartNumbers)
          .then((res) => {
            setUeImages(res.data);
            setUeOnline(true);
          })
          .catch(() => setUeOnline(false));
      }
      if (uniquePartNumbers.length === 0 || isEmpty) {
        ueImage(selectedProduct?.id, [])
          .then((res) => {
            setUeImages(res.data);
            setUeOnline(true);
          })
          .catch(() => setUeOnline(false));
      }
    }
  }, 200);

  useEffect(() => {
    setShowSideNav(false);
    return () => setShowSideNav(true);
  }, [setShowSideNav]);

  useEffect(() => {
    if (state?.quoteId) {
      refetch(state.quoteId);
      setUeImages(state?.ueImages ?? []);
    }
  }, [refetch, selectedProduct?.id, ueImage, state]);

  useEffect(() => {
    if (quoteData?.quote_options) {
      quoteData?.quote_options.forEach((option) => {
        const { name, found, partNumber } = recursiveGetOptionName(
          option,
          data?.productOptions,
          { name: '', partNumber: '' },
        );
        if (found) {
          if (option.below_the_line) {
            setValue(`options.${name}.belowTheLine`, true);
          } else {
            setValue(`options.${name}.id`, option.product_to_option_link_id);
          }
          setValue(`options.${name}.partNumber`, partNumber);
          setValue(`options.${name}.type`, option.option_type);
        }
      });
      fetchUEImages();
    } else {
      const productOptions = recursiveFlatMap(data?.productOptions ?? []);
      productOptions.forEach((option) => {
        const { name, found, partNumber } = recursiveGetOptionName(
          option,
          data?.productOptions,
          { name: '', partNumber: '' },
        );
        if (found) {
          if (option.below_the_line) {
            setValue(`options.${name}.belowTheLine`, true);
          } else {
            setValue(`options.${name}.id`, option.product_to_option_link_id);
          }
          setValue(`options.${name}.partNumber`, partNumber);
          setValue(`options.${name}.type`, option.option_type);
        }
      });
    }
  }, [quoteData?.quote_options, setValue, fetchUEImages, data?.productOptions]);

  const hasFetchedUEImages = useRef(false);

  useEffect(() => {
    if (
      hasFetchedUEImages.current ||
      overrideUnrealImages === undefined ||
      !selectedProduct?.id ||
      state?.quoteId
    ) {
      return;
    }
    hasFetchedUEImages.current = true;
    ueImage(selectedProduct?.id, [])
      .then((res) => {
        setUeImages(res.data);
        setUeOnline(true || Object.values(error).length > 0);
      })
      .catch(() => setUeOnline(false))
      .finally(() => setInitialLoad(false));
  }, [
    overrideUnrealImages,
    selectedProduct?.id,
    state?.quoteId,
    ueImage,
    error,
  ]);

  if (
    dataLoading ||
    quoteLoading ||
    loading ||
    updateConfigLoading ||
    savingQuote
  ) {
    return (
      <Styled.WrapperContainer>
        <Loader loading />
      </Styled.WrapperContainer>
    );
  }

  return (
    <PageWrapper>
      <ProductOptionsProvider
        productOptions={data?.productOptions}
        overrideUnrealImages={data?.product.override_unreal_images}
        fetchUEImagesFunc={fetchUEImages}
        ueImages={ueImages}
        showDependencies={showDependencies}
        setShowDependencies={setShowDependencies}
      >
        <FormProvider {...methods}>
          <Styled.WrapperContainer>
            <Styled.WrapperChild>
              <Styled.MainContainer>
                <Styled.SubContainer>
                  <Styled.LeftContainer>
                    <Styled.HeadingMainContainer>
                      <Styled.HeadingContainer>
                        <Heading2Text>{data?.product?.name}</Heading2Text>
                        <Styled.ConfigurationContainer>
                          <SubheaderText>{`${selectedProductCategory?.name} - ${selectedProduct?.name}`}</SubheaderText>
                        </Styled.ConfigurationContainer>
                      </Styled.HeadingContainer>
                      <FaCircle fill={ueOnline ? 'lightgreen' : 'red'} />
                    </Styled.HeadingMainContainer>
                    <Styled.CarouselContainer>
                      <ImageCarousel
                        images={
                          !overrideUnrealImages && ueImages.length > 0
                            ? ueImages
                            : data?.productGallery
                        }
                        startIndex={slideIndex}
                        onSlide={(x) => setSlideIndex(x)}
                        loading={ueImages.length === 0 && ueInitialLoad}
                      />
                      <Styled.ButtonContainer>
                        {data?.productDocuments?.map((item) => (
                          <PrimaryOutlineButton
                            key={item.id}
                            title={item.name}
                            margin="0 0 10px 10px"
                            icon={<MdOutlineFileDownload />}
                            type="button"
                            onClick={() => window.open(item.document, '_blank')}
                          />
                        ))}
                      </Styled.ButtonContainer>
                    </Styled.CarouselContainer>
                  </Styled.LeftContainer>
                  <Styled.MainOptionsContainer>
                    {data?.productOptions
                      ?.filter((option) => !option?.is_hidden)
                      ?.map((option, index) => (
                        <RenderOption
                          key={option.id}
                          node={option}
                          expandedParentOption={expandedOption === index}
                          setExpandedParentOption={() => {
                            if (expandedOption !== index) {
                              setExpandedOption(index);
                            } else {
                              setExpandedOption(null);
                            }
                          }}
                          companyId={selectedSupplier?.id}
                          name={`options.${index}`}
                          parentName="options"
                          level={0}
                          parentNode={option}
                          initialValues={quoteData?.quote_options}
                          isFirst={index === 0}
                          partNumber={
                            option.option_type === 'folder'
                              ? option.name
                              : option.part_number
                          }
                        />
                      ))}
                    {showDependencies.show && showDependencies.node.id && (
                      <DependencyModal node={showDependencies.node} />
                    )}
                  </Styled.MainOptionsContainer>
                </Styled.SubContainer>
              </Styled.MainContainer>
              <Styled.Footer>
                <Styled.StyledButton
                  back
                  type="button"
                  onClick={() =>
                    navigate('/shop/sales/select-product', {
                      state: {
                        selectedProduct,
                        selectedProductCategory,
                        selectedSupplier,
                      },
                    })
                  }
                >
                  <MdOutlineArrowBack />
                  Back
                </Styled.StyledButton>
                <Styled.StyledButton
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  disabled={loading || isEmpty}
                >
                  Next
                  <MdOutlineArrowForward />
                </Styled.StyledButton>
              </Styled.Footer>
            </Styled.WrapperChild>
          </Styled.WrapperContainer>
        </FormProvider>
      </ProductOptionsProvider>
    </PageWrapper>
  );
};

export default Configuration;
